import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Tabs,
    Tab,
    TextField,
    Button,
    Grid,
    IconButton,
    Card,
    CardContent,
    CardActions,
    Switch,
    FormControlLabel,
    Alert,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    Container,
    Paper,
    Autocomplete,
    Popover
} from '@mui/material';
import EmojiPicker from 'emoji-picker-react';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Save as SaveIcon
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';

function TabPanel({ children, value, index }) {
    return (
        <div hidden={value !== index} style={{ paddingTop: '24px' }}>
            {value === index && children}
        </div>
    );
}

const LandingPageConfig = () => {
    const [tabValue, setTabValue] = useState(0);
    const [institutionId, setInstitutionId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Main Configuration
    const [config, setConfig] = useState({
        heroTitle: '',
        heroSubtitle: '',
        featuredVideoTitle: '',
        featuredVideoAuthor: '',
        featuredVideoDepartment: '',
        featuredVideoDuration: '',
        featuredVideoViews: '',
        featuredVideoDate: '',
        featuredVideoUrl: '',
        featuredVideoThumbnail: '',
        verseText: '',
        verseReference: '',
        studentsCount: '',
        facultyCount: '',
        coursesCount: '',
        campusesCount: '',
        ctaTitle: '',
        ctaSubtitle: '',
        ctaButtonText: '',
        trialBannerText: '',
        trialBannerEnabled: true,
        previewCourseIds: []
    });
    const [availableCourses, setAvailableCourses] = useState([]);

    // Featured Articles
    const [articles, setArticles] = useState([]);
    const [articleDialog, setArticleDialog] = useState({ open: false, article: null, isEdit: false });

    // News Items
    const [newsItems, setNewsItems] = useState([]);
    const [newsDialog, setNewsDialog] = useState({ open: false, news: null, isEdit: false });

    // Quick Shortcuts
    const [shortcuts, setShortcuts] = useState([]);
    const [shortcutDialog, setShortcutDialog] = useState({ open: false, shortcut: null, isEdit: false });

    useEffect(() => {
        // Get institution ID from localStorage or user context
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (user.campusId) {
            // Fetch campus to get institution ID
            fetchInstitutionId(user.campusId);
        }
    }, []);

    useEffect(() => {
        if (institutionId) {
            loadLandingPageData();
            loadAvailableCourses();
        }
    }, [institutionId]);

    const fetchInstitutionId = async (campusId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/campuses/${campusId}`);
            setInstitutionId(response.data.institutionId);
        } catch (error) {
            console.error('Error fetching institution ID:', error);
            showSnackbar('Failed to fetch institution information', 'error');
        }
    };

    const loadLandingPageData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/landing-page/${institutionId}`);
            setConfig({
                ...(response.data.config || {}),
                previewCourseIds: Array.isArray(response.data.config?.previewCourseIds)
                    ? response.data.config.previewCourseIds.map(Number).filter(Boolean)
                    : []
            });
            setArticles(response.data.featuredArticles || []);
            setNewsItems(response.data.newsItems || []);
            setShortcuts(response.data.quickShortcuts || []);
        } catch (error) {
            console.error('Error loading landing page data:', error);
            showSnackbar('Failed to load landing page configuration', 'error');
        } finally {
            setLoading(false);
        }
    };

    const loadAvailableCourses = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}/courses`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const sortedCourses = (response.data || []).sort((a, b) =>
                (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
            );
            setAvailableCourses(sortedCourses);
        } catch (error) {
            console.error('Error loading courses for preview selector:', error);
            showSnackbar('Failed to load courses for preview selector', 'error');
        }
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleConfigChange = (field, value) => {
        setConfig(prev => ({ ...prev, [field]: value }));
    };

    const saveConfig = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            await axios.put(
                `${API_BASE_URL}/landing-page/${institutionId}/config`,
                config,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            showSnackbar('Configuration saved successfully');
        } catch (error) {
            console.error('Error saving config:', error);
            showSnackbar('Failed to save configuration', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Article CRUD
    const handleArticleSave = async (article) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (articleDialog.isEdit) {
                await axios.put(
                    `${API_BASE_URL}/landing-page/${institutionId}/articles/${article.id}`,
                    article,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                showSnackbar('Article updated successfully');
            } else {
                await axios.post(
                    `${API_BASE_URL}/landing-page/${institutionId}/articles`,
                    article,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                showSnackbar('Article created successfully');
            }
            loadLandingPageData();
            setArticleDialog({ open: false, article: null, isEdit: false });
        } catch (error) {
            console.error('Error saving article:', error);
            showSnackbar('Failed to save article', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleArticleDelete = async (articleId) => {
        if (!window.confirm('Are you sure you want to delete this article?')) return;

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            await axios.delete(
                `${API_BASE_URL}/landing-page/${institutionId}/articles/${articleId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            showSnackbar('Article deleted successfully');
            loadLandingPageData();
        } catch (error) {
            console.error('Error deleting article:', error);
            showSnackbar('Failed to delete article', 'error');
        } finally {
            setLoading(false);
        }
    };

    // News CRUD
    const handleNewsSave = async (news) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (newsDialog.isEdit) {
                await axios.put(
                    `${API_BASE_URL}/landing-page/${institutionId}/news/${news.id}`,
                    news,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                showSnackbar('News item updated successfully');
            } else {
                await axios.post(
                    `${API_BASE_URL}/landing-page/${institutionId}/news`,
                    news,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                showSnackbar('News item created successfully');
            }
            loadLandingPageData();
            setNewsDialog({ open: false, news: null, isEdit: false });
        } catch (error) {
            console.error('Error saving news:', error);
            showSnackbar('Failed to save news item', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleNewsDelete = async (newsId) => {
        if (!window.confirm('Are you sure you want to delete this news item?')) return;

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            await axios.delete(
                `${API_BASE_URL}/landing-page/${institutionId}/news/${newsId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            showSnackbar('News item deleted successfully');
            loadLandingPageData();
        } catch (error) {
            console.error('Error deleting news:', error);
            showSnackbar('Failed to delete news item', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Shortcut CRUD
    const handleShortcutSave = async (shortcut) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (shortcutDialog.isEdit) {
                await axios.put(
                    `${API_BASE_URL}/landing-page/${institutionId}/shortcuts/${shortcut.id}`,
                    shortcut,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                showSnackbar('Shortcut updated successfully');
            } else {
                await axios.post(
                    `${API_BASE_URL}/landing-page/${institutionId}/shortcuts`,
                    shortcut,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                showSnackbar('Shortcut created successfully');
            }
            loadLandingPageData();
            setShortcutDialog({ open: false, shortcut: null, isEdit: false });
        } catch (error) {
            console.error('Error saving shortcut:', error);
            showSnackbar('Failed to save shortcut', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleShortcutDelete = async (shortcutId) => {
        if (!window.confirm('Are you sure you want to delete this shortcut?')) return;

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            await axios.delete(
                `${API_BASE_URL}/landing-page/${institutionId}/shortcuts/${shortcutId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            showSnackbar('Shortcut deleted successfully');
            loadLandingPageData();
        } catch (error) {
            console.error('Error deleting shortcut:', error);
            showSnackbar('Failed to delete shortcut', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ width: '100%', p: 3, mt: 2 }}>
            <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h4" component="h1" gutterBottom fontWeight="700" color="text.primary">
                    Landing Page Configuration
                </Typography>
                <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4 }}>
                    Customize your institution's public landing page content and appearance.
                </Typography>

                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
                    <Tabs
                        value={tabValue}
                        onChange={(e, newValue) => setTabValue(newValue)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.95rem',
                                minHeight: 48,
                                px: 4
                            },
                        }}
                    >
                        <Tab label="General Settings" />
                        <Tab label="Featured Articles" />
                        <Tab label="News Items" />
                        <Tab label="Quick Shortcuts" />
                    </Tabs>
                </Box>

                {/* General Settings Tab */}
                <TabPanel value={tabValue} index={0}>
                    <Grid container spacing={4}>
                        {/* Hero Section */}
                        <Grid item xs={12}>
                            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default' }}>
                                <Typography variant="h6" gutterBottom fontWeight="600" sx={{ mb: 3 }}>
                                    Hero Section
                                </Typography>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Hero Title"
                                            value={config.heroTitle}
                                            onChange={(e) => handleConfigChange('heroTitle', e.target.value)}
                                            variant="outlined"
                                            helperText="Main headline on the landing page"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Hero Subtitle"
                                            value={config.heroSubtitle}
                                            onChange={(e) => handleConfigChange('heroSubtitle', e.target.value)}
                                            variant="outlined"
                                        />
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>

                        {/* Featured Video */}
                        <Grid item xs={12}>
                            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default' }}>
                                <Typography variant="h6" gutterBottom fontWeight="600" sx={{ mb: 3 }}>
                                    Featured Video
                                </Typography>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Video Title"
                                            value={config.featuredVideoTitle}
                                            onChange={(e) => handleConfigChange('featuredVideoTitle', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Video Author"
                                            value={config.featuredVideoAuthor}
                                            onChange={(e) => handleConfigChange('featuredVideoAuthor', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Department"
                                            value={config.featuredVideoDepartment}
                                            onChange={(e) => handleConfigChange('featuredVideoDepartment', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Duration"
                                            value={config.featuredVideoDuration}
                                            onChange={(e) => handleConfigChange('featuredVideoDuration', e.target.value)}
                                            placeholder="25:30"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Views"
                                            value={config.featuredVideoViews}
                                            onChange={(e) => handleConfigChange('featuredVideoViews', e.target.value)}
                                            placeholder="2.3K"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Date"
                                            value={config.featuredVideoDate}
                                            onChange={(e) => handleConfigChange('featuredVideoDate', e.target.value)}
                                            placeholder="2 days ago"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Video URL (optional)"
                                            value={config.featuredVideoUrl}
                                            onChange={(e) => handleConfigChange('featuredVideoUrl', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Thumbnail URL (optional)"
                                            value={config.featuredVideoThumbnail}
                                            onChange={(e) => handleConfigChange('featuredVideoThumbnail', e.target.value)}
                                        />
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>

                        {/* Verse of the Day */}
                        <Grid item xs={12}>
                            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default' }}>
                                <Typography variant="h6" gutterBottom fontWeight="600" sx={{ mb: 3 }}>
                                    Verse of the Day
                                </Typography>
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={3}
                                            label="Verse Text"
                                            value={config.verseText}
                                            onChange={(e) => handleConfigChange('verseText', e.target.value)}
                                            placeholder="Enter the scripture text..."
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Verse Reference"
                                            value={config.verseReference}
                                            onChange={(e) => handleConfigChange('verseReference', e.target.value)}
                                            placeholder="e.g. Jeremiah 29:11 (NIV)"
                                        />
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>

                        {/* Community Stats */}
                        <Grid item xs={12}>
                            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default' }}>
                                <Typography variant="h6" gutterBottom fontWeight="600" sx={{ mb: 3 }}>
                                    Community Statistics
                                </Typography>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            label="Students Count"
                                            value={config.studentsCount}
                                            onChange={(e) => handleConfigChange('studentsCount', e.target.value)}
                                            placeholder="850+"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            label="Faculty Count"
                                            value={config.facultyCount}
                                            onChange={(e) => handleConfigChange('facultyCount', e.target.value)}
                                            placeholder="45"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            label="Courses Count"
                                            value={config.coursesCount}
                                            onChange={(e) => handleConfigChange('coursesCount', e.target.value)}
                                            placeholder="25+"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            label="Campuses Count"
                                            value={config.campusesCount}
                                            onChange={(e) => handleConfigChange('campusesCount', e.target.value)}
                                            placeholder="12"
                                        />
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>

                        {/* Call to Action */}
                        <Grid item xs={12}>
                            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default' }}>
                                <Typography variant="h6" gutterBottom fontWeight="600" sx={{ mb: 3 }}>
                                    Call to Action
                                </Typography>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="CTA Title"
                                            value={config.ctaTitle}
                                            onChange={(e) => handleConfigChange('ctaTitle', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="CTA Subtitle"
                                            value={config.ctaSubtitle}
                                            onChange={(e) => handleConfigChange('ctaSubtitle', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="CTA Button Text"
                                            value={config.ctaButtonText}
                                            onChange={(e) => handleConfigChange('ctaButtonText', e.target.value)}
                                        />
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>

                        {/* Trial Banner */}
                        <Grid item xs={12}>
                            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default' }}>
                                <Typography variant="h6" gutterBottom fontWeight="600" sx={{ mb: 3 }}>
                                    Trial Banner
                                </Typography>
                                <Grid container spacing={3} alignItems="center">
                                    <Grid item xs={12} md={8}>
                                        <TextField
                                            fullWidth
                                            label="Trial Banner Text"
                                            value={config.trialBannerText}
                                            onChange={(e) => handleConfigChange('trialBannerText', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={config.trialBannerEnabled}
                                                    onChange={(e) => handleConfigChange('trialBannerEnabled', e.target.checked)}
                                                    color="primary"
                                                />
                                            }
                                            label="Enable Banner"
                                        />
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>

                        {/* Preview Courses */}
                        <Grid item xs={12}>
                            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default' }}>
                                <Typography variant="h6" gutterBottom fontWeight="600" sx={{ mb: 1 }}>
                                    Preview Courses
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    Choose the larger course previews shown on the public landing page. Selected order is preserved.
                                </Typography>
                                <Autocomplete
                                    multiple
                                    options={availableCourses}
                                    value={availableCourses.filter(course =>
                                        (config.previewCourseIds || []).includes(Number(course.id))
                                    )}
                                    onChange={(event, selectedCourses) =>
                                        handleConfigChange('previewCourseIds', selectedCourses.map(course => Number(course.id)))
                                    }
                                    getOptionLabel={(course) => course.name || 'Untitled Course'}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip
                                                label={option.name}
                                                {...getTagProps({ index })}
                                                key={option.id}
                                                color="primary"
                                                variant="outlined"
                                            />
                                        ))
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Featured Preview Courses"
                                            placeholder="Search and select courses"
                                            helperText="Leave empty to hide the preview course section."
                                        />
                                    )}
                                />
                            </Paper>
                        </Grid>

                        {/* Save Button */}
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<SaveIcon />}
                                    onClick={saveConfig}
                                    disabled={loading}
                                    size="large"
                                    sx={{ px: 4, py: 1.5, borderRadius: 2, fontWeight: 600 }}
                                >
                                    {loading ? 'Saving...' : 'Save Configuration'}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Featured Articles Tab */}
                <TabPanel value={tabValue} index={1}>
                    <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">
                            Manage Featured Articles
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setArticleDialog({
                                open: true, article: {
                                    title: '',
                                    excerpt: '',
                                    category: '',
                                    readTime: '5 min read',
                                    imageEmoji: 'Book',
                                    imageUrl: '',
                                    articleUrl: '',
                                    order: articles.length,
                                    isActive: true
                                }, isEdit: false
                            })}
                            sx={{ borderRadius: 2, textTransform: 'none' }}
                        >
                            Add Article
                        </Button>
                    </Box>
                    <Grid container spacing={3}>
                        {articles.map((article) => (
                            <Grid item xs={12} md={6} key={article.id}>
                                <Card elevation={0} variant="outlined" sx={{
                                    height: '100%',
                                    borderRadius: 3,
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                                    }
                                }}>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="h5" component="span" sx={{ lineHeight: 1 }}>
                                                    {article.imageEmoji === 'Book' ? '📖' : (article.imageEmoji || '📖')}
                                                </Typography>
                                                <Chip
                                                    label={article.category}
                                                    size="small"
                                                    sx={{
                                                        borderRadius: 1,
                                                        bgcolor: 'primary.50',
                                                        color: 'primary.700',
                                                        fontWeight: 600
                                                    }}
                                                />
                                            </Box>
                                            <Chip
                                                label={article.isActive ? 'Active' : 'Inactive'}
                                                size="small"
                                                color={article.isActive ? 'success' : 'default'}
                                                variant={article.isActive ? 'filled' : 'outlined'}
                                            />
                                        </Box>
                                        <Typography variant="h6" gutterBottom fontWeight="600" sx={{ minHeight: 64 }}>
                                            {article.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{
                                            mb: 2,
                                            display: '-webkit-box',
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            minHeight: 60
                                        }}>
                                            {article.excerpt}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', fontSize: '0.875rem' }}>
                                            <span>{article.readTime}</span>
                                            <span>•</span>
                                            <span>Order: {article.order}</span>
                                        </Box>
                                    </CardContent>
                                    <CardActions sx={{ px: 2, pb: 2, justifyContent: 'flex-end', borderTop: '1px solid', borderColor: 'divider' }}>
                                        <Button
                                            size="small"
                                            startIcon={<EditIcon />}
                                            onClick={() => setArticleDialog({ open: true, article, isEdit: true })}
                                            sx={{ mr: 1 }}
                                        >
                                            Edit
                                        </Button>
                                        <Button
                                            size="small"
                                            color="error"
                                            startIcon={<DeleteIcon />}
                                            onClick={() => handleArticleDelete(article.id)}
                                        >
                                            Delete
                                        </Button>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))}
                        {articles.length === 0 && (
                            <Grid item xs={12}>
                                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2, bgcolor: 'background.default' }} variant="outlined">
                                    <Typography color="text.secondary">No featured articles found. Add one to get started.</Typography>
                                </Paper>
                            </Grid>
                        )}
                    </Grid>
                </TabPanel>

                {/* News Items Tab */}
                <TabPanel value={tabValue} index={2}>
                    <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">
                            Manage News & Announcements
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setNewsDialog({
                                open: true, news: {
                                    title: '',
                                    excerpt: '',
                                    category: '',
                                    dateText: '1 day ago',
                                    fullContentUrl: '',
                                    order: newsItems.length,
                                    isActive: true
                                }, isEdit: false
                            })}
                            sx={{ borderRadius: 2, textTransform: 'none' }}
                        >
                            Add News Item
                        </Button>
                    </Box>
                    <Grid container spacing={3}>
                        {newsItems.map((news) => (
                            <Grid item xs={12} md={6} key={news.id}>
                                <Card elevation={0} variant="outlined" sx={{
                                    height: '100%',
                                    borderRadius: 3,
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                                    }
                                }}>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
                                            <Chip label={news.category} size="small" color="secondary" variant="outlined" />
                                            <Typography variant="caption" color="text.secondary">{news.dateText}</Typography>
                                        </Box>
                                        <Typography variant="h6" gutterBottom fontWeight="600">
                                            {news.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{
                                            mb: 2,
                                            display: '-webkit-box',
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden'
                                        }}>
                                            {news.excerpt}
                                        </Typography>
                                        <Box sx={{ mt: 1 }}>
                                            <Chip
                                                label={news.isActive ? 'Active' : 'Inactive'}
                                                size="small"
                                                color={news.isActive ? 'success' : 'default'}
                                                variant={news.isActive ? 'filled' : 'outlined'}
                                                sx={{ mr: 1 }}
                                            />
                                            <Typography variant="caption" color="text.secondary" component="span">
                                                Order: {news.order}
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                    <CardActions sx={{ px: 2, pb: 2, justifyContent: 'flex-end', borderTop: '1px solid', borderColor: 'divider' }}>
                                        <Button
                                            size="small"
                                            startIcon={<EditIcon />}
                                            onClick={() => setNewsDialog({ open: true, news, isEdit: true })}
                                            sx={{ mr: 1 }}
                                        >
                                            Edit
                                        </Button>
                                        <Button
                                            size="small"
                                            color="error"
                                            startIcon={<DeleteIcon />}
                                            onClick={() => handleNewsDelete(news.id)}
                                        >
                                            Delete
                                        </Button>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))}
                        {newsItems.length === 0 && (
                            <Grid item xs={12}>
                                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2, bgcolor: 'background.default' }} variant="outlined">
                                    <Typography color="text.secondary">No news items found. Add one to get started.</Typography>
                                </Paper>
                            </Grid>
                        )}
                    </Grid>
                </TabPanel>

                {/* Quick Shortcuts Tab */}
                <TabPanel value={tabValue} index={3}>
                    <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">
                            Manage Quick Shortcuts
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setShortcutDialog({
                                open: true, shortcut: {
                                    title: '',
                                    icon: 'Book',
                                    actionType: 'alert',
                                    actionValue: '',
                                    order: shortcuts.length,
                                    isActive: true
                                }, isEdit: false
                            })}
                            sx={{ borderRadius: 2, textTransform: 'none' }}
                        >
                            Add Shortcut
                        </Button>
                    </Box>
                    <Grid container spacing={3}>
                        {shortcuts.map((shortcut) => (
                            <Grid item xs={12} sm={6} md={3} key={shortcut.id}>
                                <Card elevation={0} variant="outlined" sx={{
                                    height: '100%',
                                    borderRadius: 3,
                                    textAlign: 'center',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                                    }
                                }}>
                                    <CardContent>
                                        <Box sx={{ mb: 2, fontSize: '2.5rem' }}>
                                            {shortcut.icon === 'Book' ? '📚' : (shortcut.icon || '📚')}
                                        </Box>
                                        <Typography variant="h6" gutterBottom fontWeight="600">
                                            {shortcut.title}
                                        </Typography>
                                        <Chip
                                            label={shortcut.actionType}
                                            size="small"
                                            variant="outlined"
                                            sx={{ mb: 2, textTransform: 'capitalize' }}
                                        />
                                        <Box>
                                            <Chip
                                                label={shortcut.isActive ? 'Active' : 'Inactive'}
                                                size="small"
                                                color={shortcut.isActive ? 'success' : 'default'}
                                            />
                                        </Box>
                                    </CardContent>
                                    <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                                        <IconButton
                                            size="small"
                                            onClick={() => setShortcutDialog({ open: true, shortcut, isEdit: true })}
                                            color="primary"
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleShortcutDelete(shortcut.id)}
                                            color="error"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))}
                        {shortcuts.length === 0 && (
                            <Grid item xs={12}>
                                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2, bgcolor: 'background.default' }} variant="outlined">
                                    <Typography color="text.secondary">No shortcuts found. Add one to get started.</Typography>
                                </Paper>
                            </Grid>
                        )}
                    </Grid>
                </TabPanel>

                {/* Article Dialog */}
                <ArticleDialog
                    open={articleDialog.open}
                    article={articleDialog.article}
                    isEdit={articleDialog.isEdit}
                    onClose={() => setArticleDialog({ open: false, article: null, isEdit: false })}
                    onSave={handleArticleSave}
                />

                {/* News Dialog */}
                <NewsDialog
                    open={newsDialog.open}
                    news={newsDialog.news}
                    isEdit={newsDialog.isEdit}
                    onClose={() => setNewsDialog({ open: false, news: null, isEdit: false })}
                    onSave={handleNewsSave}
                />

                {/* Shortcut Dialog */}
                <ShortcutDialog
                    open={shortcutDialog.open}
                    shortcut={shortcutDialog.shortcut}
                    isEdit={shortcutDialog.isEdit}
                    onClose={() => setShortcutDialog({ open: false, shortcut: null, isEdit: false })}
                    onSave={handleShortcutSave}
                />

                {/* Snackbar */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert
                        severity={snackbar.severity}
                        onClose={() => setSnackbar({ ...snackbar, open: false })}
                        variant="filled"
                        sx={{ width: '100%' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Paper>
        </Box>
    );
};

// Emoji Picker Component
function EmojiPickerField({ label, value, onChange }) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const onEmojiClick = (emojiObject) => {
        onChange(emojiObject.emoji);
        handleClose();
    };

    return (
        <Box>
            <TextField
                fullWidth
                label={label}
                value={value}
                onClick={handleClick}
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <IconButton onClick={handleClick} edge="end">
                            <span style={{ fontSize: '1.5rem' }}>{value || '😀'}</span>
                        </IconButton>
                    )
                }}
            />
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
            >
                <EmojiPicker onEmojiClick={onEmojiClick} />
            </Popover>
        </Box>
    );
}

// Article Dialog Component
function ArticleDialog({ open, article, isEdit, onClose, onSave }) {
    const [formData, setFormData] = useState(article || {});

    useEffect(() => {
        setFormData(article || {});
    }, [article]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = () => {
        onSave(formData);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{isEdit ? 'Edit Article' : 'Add Article'}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Title"
                            value={formData.title || ''}
                            onChange={(e) => handleChange('title', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Excerpt"
                            value={formData.excerpt || ''}
                            onChange={(e) => handleChange('excerpt', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Category"
                            value={formData.category || ''}
                            onChange={(e) => handleChange('category', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Read Time"
                            value={formData.readTime || ''}
                            onChange={(e) => handleChange('readTime', e.target.value)}
                            placeholder="5 min read"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <EmojiPickerField
                            label="Image Emoji"
                            value={formData.imageEmoji || ''}
                            onChange={(val) => handleChange('imageEmoji', val)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Image URL (optional)"
                            value={formData.imageUrl || ''}
                            onChange={(e) => handleChange('imageUrl', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Article URL (optional)"
                            value={formData.articleUrl || ''}
                            onChange={(e) => handleChange('articleUrl', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Order"
                            value={formData.order || 0}
                            onChange={(e) => handleChange('order', parseInt(e.target.value))}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.isActive || false}
                                    onChange={(e) => handleChange('isActive', e.target.checked)}
                                />
                            }
                            label="Active"
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" color="primary">
                    {isEdit ? 'Update' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// News Dialog Component
function NewsDialog({ open, news, isEdit, onClose, onSave }) {
    const [formData, setFormData] = useState(news || {});

    useEffect(() => {
        setFormData(news || {});
    }, [news]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = () => {
        onSave(formData);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{isEdit ? 'Edit News Item' : 'Add News Item'}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Title"
                            value={formData.title || ''}
                            onChange={(e) => handleChange('title', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Excerpt"
                            value={formData.excerpt || ''}
                            onChange={(e) => handleChange('excerpt', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Category"
                            value={formData.category || ''}
                            onChange={(e) => handleChange('category', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Date Text"
                            value={formData.dateText || ''}
                            onChange={(e) => handleChange('dateText', e.target.value)}
                            placeholder="1 day ago"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Full Content URL (optional)"
                            value={formData.fullContentUrl || ''}
                            onChange={(e) => handleChange('fullContentUrl', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Order"
                            value={formData.order || 0}
                            onChange={(e) => handleChange('order', parseInt(e.target.value))}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.isActive || false}
                                    onChange={(e) => handleChange('isActive', e.target.checked)}
                                />
                            }
                            label="Active"
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" color="primary">
                    {isEdit ? 'Update' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// Shortcut Dialog Component
function ShortcutDialog({ open, shortcut, isEdit, onClose, onSave }) {
    const [formData, setFormData] = useState(shortcut || {});

    useEffect(() => {
        setFormData(shortcut || {});
    }, [shortcut]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = () => {
        onSave(formData);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isEdit ? 'Edit Shortcut' : 'Add Shortcut'}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Title"
                            value={formData.title || ''}
                            onChange={(e) => handleChange('title', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <EmojiPickerField
                            label="Icon (Emoji)"
                            value={formData.icon || ''}
                            onChange={(val) => handleChange('icon', val)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel>Action Type</InputLabel>
                            <Select
                                value={formData.actionType || 'alert'}
                                onChange={(e) => handleChange('actionType', e.target.value)}
                                label="Action Type"
                            >
                                <MenuItem value="alert">Alert Message</MenuItem>
                                <MenuItem value="navigate">Navigate to Route</MenuItem>
                                <MenuItem value="external_link">External Link</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Action Value"
                            value={formData.actionValue || ''}
                            onChange={(e) => handleChange('actionValue', e.target.value)}
                            placeholder={
                                formData.actionType === 'alert' ? 'Coming Soon message' :
                                    formData.actionType === 'navigate' ? '/route-path' :
                                        'https://example.com'
                            }
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Order"
                            value={formData.order || 0}
                            onChange={(e) => handleChange('order', parseInt(e.target.value))}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.isActive || false}
                                    onChange={(e) => handleChange('isActive', e.target.checked)}
                                />
                            }
                            label="Active"
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" color="primary">
                    {isEdit ? 'Update' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default LandingPageConfig;
